import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, Mail, Clock, MessageCircle, Plane, ExternalLink } from "lucide-react";
import { WA_LINK } from "@/data";

const inView = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

export default function Contact() {
  const contactItems = [
    {
      icon: <MapPin size={22} className="text-[#FF4500]" />,
      label: "Studio Address",
      value: "Shop No. 1B, Venus Tower, Behind Axis Bank, Veera Desai Road, Andheri West, Mumbai — 400053",
      link: null,
    },
    {
      icon: <Phone size={22} className="text-[#FF4500]" />,
      label: "Phone",
      value: "+91 90042 50500",
      link: "tel:+919004250500",
    },
    {
      icon: <Mail size={22} className="text-[#FF4500]" />,
      label: "Email",
      value: "jraj06272@gmail.com",
      link: "mailto:jraj06272@gmail.com",
    },
    {
      icon: <Clock size={22} className="text-[#FF4500]" />,
      label: "Working Hours",
      value: "8:00 AM – 11:30 PM (All Days) | Late night hotel service on request",
      link: null,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Contact & Book Massage | Raj Muscle Care Studio | Andheri West, Mumbai</title>
        <meta name="description" content="Book a massage session at Raj Muscle Care Studio, Andheri West Mumbai. Call +91 90042 50500 or WhatsApp. Studio address: Shop No. 1B, Venus Tower, Veera Desai Road. 15 mins from Mumbai Airport." />
        <meta name="keywords" content="massage studio Andheri West, book massage Mumbai, contact massage therapist Mumbai, massage near Mumbai airport address, massage studio location Mumbai" />
        <link rel="canonical" href="https://rajmusclecarestudio.com/contact" />
        <meta property="og:title" content="Contact Raj Muscle Care Studio | Book Massage in Mumbai" />
        <meta property="og:description" content="Book a massage in Andheri West, Mumbai. Call +91 90042 50500. 15 mins from Mumbai Airport. Studio, home & hotel service available." />
        <meta property="og:url" content="https://rajmusclecarestudio.com/contact" />
      </Helmet>
      <div className="min-h-screen bg-[#050505]">
      {/* Page Hero */}
      <div className="relative pt-32 pb-12 text-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF4500]/5 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-80 h-80 bg-[#FF4500]/6 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-3xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-3 font-inter">
              Get in Touch
            </p>
            <h1 className="text-4xl sm:text-5xl text-white font-black mb-5 leading-tight">
              Contact
              <br />
              <span className="text-[#FF4500]">Raj Muscle Care</span>
            </h1>
            <p className="text-gray-400 font-inter text-base">
              Book a session or enquire about our services. We're available 8 AM – 11:30 PM, all days.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <section className="pb-16 sm:pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Info */}
            <motion.div {...inView} transition={{ duration: 0.6 }} className="flex flex-col gap-4">
              {/* Airport proximity badge */}
              <div className="flex items-center gap-3 bg-[#FF4500]/10 border border-[#FF4500]/20 rounded-2xl p-4">
                <Plane size={20} className="text-[#FF4500] shrink-0" />
                <div>
                  <p className="text-[#FF4500] font-semibold text-sm font-inter">
                    Just 15 Minutes from Mumbai Airport
                  </p>
                  <p className="text-gray-400 text-xs font-inter mt-0.5">
                    Ideal for travellers — quick access from T1 &amp; T2 terminals
                  </p>
                </div>
              </div>

              {/* Contact cards */}
              {contactItems.map((item, i) => (
                <div
                  key={i}
                  className="bg-[#111111] rounded-2xl p-5 border border-white/10 flex gap-4 items-start hover:border-[#FF4500]/30 transition-colors"
                  data-testid={`contact-item-${i}`}
                >
                  <div className="mt-0.5 shrink-0">{item.icon}</div>
                  <div>
                    <p className="text-gray-500 text-xs font-inter uppercase tracking-widest mb-1">
                      {item.label}
                    </p>
                    {item.link ? (
                      <a
                        href={item.link}
                        className="text-white text-sm font-inter leading-relaxed hover:text-[#FF4500] transition-colors"
                        data-testid={`contact-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-white text-sm font-inter leading-relaxed">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* CTA Buttons */}
              <div className="flex flex-col gap-3 mt-2">
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#FF4500] hover:bg-[#e63d00] text-white font-semibold py-4 rounded-full transition-all hover:scale-105 min-h-[56px] text-base"
                  data-testid="contact-book-whatsapp-btn"
                >
                  <MessageCircle size={20} />
                  Book on WhatsApp
                </a>
                <a
                  href="tel:+919004250500"
                  className="flex items-center justify-center gap-2 border border-white/30 text-white font-semibold py-4 rounded-full transition-all hover:bg-white/5 min-h-[56px] text-base"
                  data-testid="contact-call-btn"
                >
                  <Phone size={20} />
                  Call +91 90042 50500
                </a>
                <a
                  href="mailto:jraj06272@gmail.com"
                  className="flex items-center justify-center gap-2 border border-white/30 text-white font-semibold py-4 rounded-full transition-all hover:bg-white/5 min-h-[56px] text-base"
                  data-testid="contact-email-btn"
                >
                  <Mail size={20} />
                  Email Us
                </a>
              </div>
            </motion.div>

            {/* Map + Social */}
            <motion.div
              {...inView}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="flex flex-col gap-4"
            >
              {/* Google Maps Embed */}
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#111111]">
                <div className="p-4 flex items-center justify-between border-b border-white/10">
                  <h3 className="text-white font-semibold text-sm font-inter">Find Us on Map</h3>
                  <a
                    href="https://g.co/kgs/n3rSyxM"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[#FF4500] text-xs font-inter font-semibold hover:underline"
                    data-testid="map-open-link"
                  >
                    Open in Maps <ExternalLink size={12} />
                  </a>
                </div>
                <iframe
                  title="Raj Muscle Care Studio Location"
                  src="https://maps.google.com/maps?q=Raj+Muscle+Care+Studio,+Veera+Desai+Road,+Andheri+West,+Mumbai&output=embed&z=15"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="opacity-90"
                  data-testid="google-maps-embed"
                />
              </div>

              {/* Social + Google */}
              <div className="bg-[#111111] rounded-2xl p-6 border border-white/10">
                <h3 className="text-white font-semibold mb-4 text-sm font-inter uppercase tracking-widest">
                  Find Us Online
                </h3>
                <div className="flex flex-col gap-3">
                  <a
                    href="https://g.co/kgs/n3rSyxM"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#FF4500]/30 rounded-xl px-4 py-3 transition-all"
                    data-testid="contact-google-link"
                  >
                    <span className="text-white text-sm font-inter font-medium">Google Business Profile</span>
                    <ExternalLink size={14} className="text-gray-400" />
                  </a>
                  <a
                    href="https://www.instagram.com/musclecarestudio/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#FF4500]/30 rounded-xl px-4 py-3 transition-all"
                    data-testid="contact-instagram-link"
                  >
                    <span className="text-white text-sm font-inter font-medium">Instagram @musclecarestudio</span>
                    <ExternalLink size={14} className="text-gray-400" />
                  </a>
                  <a
                    href="https://jsdl.in/RSL-ELD1776084352"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#FF4500]/30 rounded-xl px-4 py-3 transition-all"
                    data-testid="contact-justdial-link"
                  >
                    <span className="text-white text-sm font-inter font-medium">JustDial Listing</span>
                    <ExternalLink size={14} className="text-gray-400" />
                  </a>
                  <a
                    href="https://g.co/kgs/vFKFnSw"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between bg-[#FF4500]/10 border border-[#FF4500]/20 hover:border-[#FF4500]/50 rounded-xl px-4 py-3 transition-all"
                    data-testid="contact-reviews-link"
                  >
                    <span className="text-[#FF4500] text-sm font-inter font-semibold">See Google Reviews</span>
                    <ExternalLink size={14} className="text-[#FF4500]" />
                  </a>
                </div>
              </div>

              {/* Privacy note */}
              <div className="bg-[#111111] rounded-2xl p-5 border border-white/10">
                <p className="text-gray-400 text-sm font-inter text-center leading-relaxed">
                  <span className="text-[#FF4500] font-semibold">Privacy Promise:</span>{" "}
                  100% Discreet &amp; Confidential Massage Service in Mumbai
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
